'use client'

import type React from 'react'
import { useEffect, useState } from 'react'
import { useUser } from '@clerk/nextjs'
import { X, Loader2, CheckCircle2, CalendarClock } from 'lucide-react'
import { WEBINAR, isWebinarLive } from '@/lib/webinar'

/** Banner height in px — must match the `top`/`pt`/`mt` offsets in the
 * landing and dashboard layouts (Tailwind `*-10` = 2.5rem = 40px). */
export const WEBINAR_BANNER_HEIGHT = 40

const STORAGE_KEY = `webinar_registered_${WEBINAR.id}`
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

type Status = 'idle' | 'expanded' | 'submitting' | 'done' | 'error'

interface WebinarBannerProps {
  source: 'landing' | 'dashboard'
  /** Extra classes for the fixed outer bar (e.g. left offset on dashboard). */
  className?: string
}

export default function WebinarBanner({ source, className = '' }: WebinarBannerProps) {
  const { isSignedIn } = useUser()
  const [status, setStatus] = useState<Status>('idle')
  const [email, setEmail] = useState('')
  const [error, setError] = useState('')
  const [mounted, setMounted] = useState(false)

  useEffect(() => {
    setMounted(true)
    try {
      if (localStorage.getItem(STORAGE_KEY)) setStatus('done')
    } catch {}
  }, [])

  // Auto-hide once the webinar has ended. Computed identically on server/client.
  if (!isWebinarLive()) return null

  async function submit(withEmail?: string) {
    setStatus('submitting')
    setError('')
    try {
      const res = await fetch('/api/webinar/register', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ source, email: withEmail }),
      })
      const data = await res.json().catch(() => ({}))
      if (!res.ok || !data.ok) {
        throw new Error(data.error || 'failed')
      }
      try {
        localStorage.setItem(STORAGE_KEY, '1')
      } catch {}
      setStatus('done')
    } catch (e: any) {
      setError(e?.message === 'invalid-email' ? 'Enter a valid email' : 'Something went wrong')
      setStatus('error')
    }
  }

  function onRegisterClick() {
    if (isSignedIn) {
      submit() // server uses the account email
    } else {
      setStatus('expanded')
    }
  }

  function onGuestSubmit(e: React.FormEvent) {
    e.preventDefault()
    const value = email.trim()
    if (!EMAIL_RE.test(value)) {
      setError('Enter a valid email')
      setStatus('error')
      return
    }
    submit(value)
  }

  return (
    <div
      className={`fixed top-0 right-0 left-0 z-[70] flex h-10 items-center justify-center gap-3 bg-gradient-to-r from-[#F6542E] via-[#ff6b3d] to-[#1c1917] px-3 text-white shadow-sm ${className}`}
      role="region"
      aria-label="Webinar announcement"
    >
      <CalendarClock className="hidden h-4 w-4 flex-shrink-0 sm:block" aria-hidden="true" />

      <p className="truncate text-[13px] font-medium leading-none">
        <span className="font-semibold">Live Webinar</span>
        <span className="mx-1.5 opacity-60">·</span>
        <span className="hidden sm:inline">{WEBINAR.title} · </span>
        <span>{WEBINAR.timeLabelShort}</span>
      </p>

      {(status === 'idle' || status === 'submitting') && (
        <button
          onClick={onRegisterClick}
          disabled={status === 'submitting'}
          className="flex flex-shrink-0 items-center gap-1.5 rounded-full bg-white px-3 py-1 text-[12px] font-semibold text-[#1c1917] transition hover:bg-white/90 disabled:opacity-70"
        >
          {status === 'submitting' && <Loader2 className="h-3 w-3 animate-spin" aria-hidden="true" />}
          {status === 'submitting' ? 'Registering…' : 'Register free'}
        </button>
      )}

      {(status === 'expanded' || status === 'error') && !isSignedIn && (
        <form onSubmit={onGuestSubmit} className="flex flex-shrink-0 items-center gap-1.5">
          <input
            type="email"
            value={email}
            onChange={(e) => {
              setEmail(e.target.value)
              if (status === 'error') setStatus('expanded')
            }}
            autoFocus
            placeholder="you@email.com"
            aria-label="Email address"
            className="h-7 w-[150px] rounded-full bg-white/95 px-3 text-[12px] text-[#1c1917] placeholder:text-[#1c1917]/40 focus:outline-none sm:w-[180px]"
          />
          <button
            type="submit"
            className="h-7 flex-shrink-0 rounded-full bg-[#1c1917] px-3 text-[12px] font-semibold text-white transition hover:bg-[#1c1917]/90"
          >
            Sign me up
          </button>
        </form>
      )}

      {status === 'error' && (
        <span className="hidden flex-shrink-0 text-[12px] text-white/90 sm:inline">{error}</span>
      )}

      {status === 'done' && (
        <span className="flex flex-shrink-0 items-center gap-1.5 text-[12px] font-semibold">
          <CheckCircle2 className="h-3.5 w-3.5" aria-hidden="true" />
          You're registered — check your email
        </span>
      )}

      {status === 'done' && mounted && (
        <button
          onClick={() => setStatus('idle')}
          aria-label="Dismiss"
          className="absolute right-2 rounded-full p-1 text-white/70 transition hover:text-white"
        >
          <X className="h-3.5 w-3.5" aria-hidden="true" />
        </button>
      )}
    </div>
  )
}
